import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import './Journal.css';

const fN = (n, d=2) => n != null && !isNaN(n) ? Number(n).toFixed(d) : '--';
const fC = n => (n < 0 ? '-$' : '$') + Math.abs(n).toLocaleString('en-US', { minimumFractionDigits:2, maximumFractionDigits:2 });
function pnl(t) { return (t.exit - t.entry) * t.shares * (t.side === 'short' ? -1 : 1); }

const SETUPS = ['VWAP Reclaim','Breakout','Pullback','Gap & Go','Reversal','Momentum','Short Squeeze','Float Rotation','Other'];
const EMOTIONS = ['Confident','Hesitant','FOMO','Revenge Trade','Overtrading','Disciplined','Impatient','Fearful','Greedy','Calm & Focused'];
const RULES = ['Waited for Setup','Respected Stop','Sized Correctly','Followed Plan','Cut Loss Early','Let Winner Run','Chased Entry','Moved Stop'];

export default function Journal({ user }) {
  const [view, setView] = useState('stats');
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showCSV, setShowCSV] = useState(false);
  const [filterSym, setFilterSym] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const [mSym, setMSym] = useState('');
  const [mDate, setMDate] = useState(new Date().toISOString().split('T')[0]);
  const [mSide, setMSide] = useState('long');
  const [mSetup, setMSetup] = useState('Breakout');
  const [mEntry, setMEntry] = useState('');
  const [mExit, setMExit] = useState('');
  const [mShares, setMShares] = useState('');
  const [mSl, setMSl] = useState('');
  const [mBroker, setMBroker] = useState('thinkorswim');
  const [mNotes, setMNotes] = useState('');
  const [mEmotions, setMEmotions] = useState([]);
  const [mRules, setMRules] = useState([]);

  function showToast(msg, win=true) { setToast({msg,win}); setTimeout(()=>setToast(null),3000); }
  function toggleEmotion(e) { setMEmotions(prev=>prev.includes(e)?prev.filter(x=>x!==e):[...prev,e]); }
  function toggleRule(r) { setMRules(prev=>prev.includes(r)?prev.filter(x=>x!==r):[...prev,r]); }

  // Load trades from Supabase
  useEffect(()=>{
    async function loadTrades() {
      setLoading(true);
      if(!user?.id) { setLoading(false); return; }
      const { data, error } = await supabase
        .from('trades')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      if(error) console.error('Load trades error:', error);
      else setTrades(data.map(t=>({
        ...t,
        emotions: t.emotions ? t.emotions.split(',') : [],
        rules: t.rules ? t.rules.split(',') : [],
      })));
      setLoading(false);
    }
    loadTrades();
  },[user]);

  async function saveTrade() {
    if(!mSym||!mEntry||!mExit||!mShares) { showToast('Fill in Symbol, Entry, Exit & Shares', false); return; }
    if(!user?.id) { showToast('Please sign in to save trades', false); return; }
    setSaving(true);
    const trade = {
      user_id: user.id,
      sym: mSym.toUpperCase(),
      date: mDate,
      side: mSide,
      setup: mSetup,
      entry: parseFloat(mEntry),
      exit: parseFloat(mExit),
      shares: parseInt(mShares),
      broker: mBroker,
      sl: parseFloat(mSl)||null,
      notes: mNotes,
      emotions: mEmotions.join(','),
      rules: mRules.join(','),
    };
    const { data, error } = await supabase.from('trades').insert([trade]).select();
    if(error) { showToast('Error saving trade: '+error.message, false); }
    else {
      const saved = { ...data[0], emotions: mEmotions, rules: mRules };
      setTrades(prev=>[saved,...prev]);
      setShowModal(false);
      setMSym('');setMEntry('');setMExit('');setMShares('');setMSl('');setMNotes('');setMEmotions([]);setMRules([]);
      showToast('Trade saved: '+trade.sym+' '+fC(pnl(trade)), pnl(trade)>=0);
    }
    setSaving(false);
  }

  async function deleteTrade(id) {
    const { error } = await supabase.from('trades').delete().eq('id', id);
    if(!error) { setTrades(prev=>prev.filter(t=>t.id!==id)); showToast('Trade deleted', true); }
  }

  function fakeImport() {
    setShowCSV(false);
    showToast('CSV import coming soon!', true);
  }

  const wins = trades.filter(t=>pnl(t)>0);
  const losses = trades.filter(t=>pnl(t)<0);
  const totalPnl = trades.reduce((s,t)=>s+pnl(t),0);
  const wr = trades.length?(wins.length/trades.length*100).toFixed(1):0;
  const avgW = wins.length?wins.reduce((s,t)=>s+pnl(t),0)/wins.length:0;
  const avgL = losses.length?losses.reduce((s,t)=>s+pnl(t),0)/losses.length:0;
  const rr = avgL?Math.abs(avgW/avgL).toFixed(2):'--';
  const pf = losses.reduce((s,t)=>s+pnl(t),0)?Math.abs(wins.reduce((s,t)=>s+pnl(t),0)/losses.reduce((s,t)=>s+pnl(t),0)).toFixed(2):'--';
  const emotionCount = {};
  trades.forEach(t=>(t.emotions||[]).forEach(e=>{emotionCount[e]=(emotionCount[e]||0)+1;}));
  const setupWR = {};
  SETUPS.forEach(s=>{const st=trades.filter(t=>t.setup===s);if(st.length)setupWR[s]={wr:(st.filter(t=>pnl(t)>0).length/st.length*100).toFixed(0),n:st.length};});
  const days=['Mon','Tue','Wed','Thu','Fri'].map((d,i)=>({d,p:trades.filter((_,ti)=>ti%5===i).reduce((s,t)=>s+pnl(t),0)}));
  const maxD=Math.max(...days.map(d=>Math.abs(d.p)),1);

  const year=new Date().getFullYear(),month=new Date().getMonth();
  const monthNames=['January','February','March','April','May','June','July','August','September','October','November','December'];
  const fd=new Date(year,month,1).getDay(),dim=new Date(year,month+1,0).getDate();
  const dd={};
  trades.forEach(t=>{if(!dd[t.date])dd[t.date]={p:0,n:0};dd[t.date].p+=pnl(t);dd[t.date].n++;});
  const monthPnl=Object.values(dd).reduce((s,d)=>s+d.p,0);
  const greenDays=Object.values(dd).filter(d=>d.p>0).length;
  const redDays=Object.values(dd).filter(d=>d.p<0).length;
  const filtered=filterSym?trades.filter(t=>t.sym.includes(filterSym.toUpperCase())):trades;

  if(loading) return(
    <div style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',background:'#0d1117',flexDirection:'column',gap:12}}>
      <div className="cp-spinner"></div>
      <div style={{fontSize:12,color:'#484f58'}}>Loading your trade journal...</div>
    </div>
  );

  if(!user) return(
    <div style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',background:'#0d1117',flexDirection:'column',gap:12}}>
      <div style={{fontSize:24}}>📓</div>
      <div style={{fontSize:14,fontWeight:700,color:'#e6edf3'}}>Sign in to access your Trade Journal</div>
      <div style={{fontSize:12,color:'#484f58'}}>Your trades are saved securely to your account</div>
    </div>
  );

  return (
    <div className="jn-page">
      <div className="jn-topnav">
        <div className="jn-view-tabs">
          {[['stats','📊 Stats'],['calendar','📅 Calendar'],['trades','📋 Trade Log']].map(([v,l])=>(
            <div key={v} className={`jn-vtab ${view===v?'active':''}`} onClick={()=>setView(v)}>{l}</div>
          ))}
        </div>
        <div className="jn-actions">
          <button className="jn-btn" onClick={()=>setShowCSV(true)}>⬆ Import CSV</button>
          <button className="jn-btn prim" onClick={()=>setShowModal(true)}>+ Log Trade</button>
        </div>
      </div>

      {/* STATS VIEW */}
      {view==='stats' && (
        <div className="jn-content">
          {trades.length===0?(
            <div style={{textAlign:'center',padding:60,color:'#484f58'}}>
              <div style={{fontSize:40,marginBottom:12}}>📓</div>
              <div style={{fontSize:16,fontWeight:700,color:'#e6edf3',marginBottom:8}}>No trades yet</div>
              <div style={{fontSize:12,marginBottom:20}}>Start logging your trades to see stats here</div>
              <button className="jn-btn prim" onClick={()=>setShowModal(true)}>+ Log Your First Trade</button>
            </div>
          ):(
            <>
              <div className="jn-stats-grid">
                <div className="jn-stat-card"><div className="jsc-label">Total P&L</div><div className={`jsc-val ${totalPnl>=0?'gp':'rp'}`}>{fC(totalPnl)}</div><div className="jsc-sub">{trades.length} total trades</div></div>
                <div className="jn-stat-card"><div className="jsc-label">Win Rate</div><div className="jsc-val gp">{wr}%</div><div className="jsc-sub">{wins.length}W · {losses.length}L</div></div>
                <div className="jn-stat-card"><div className="jsc-label">Avg R:R</div><div className="jsc-val yp">{rr}:1</div><div className="jsc-sub">W: {fC(avgW)} · L: {fC(avgL)}</div></div>
                <div className="jn-stat-card"><div className="jsc-label">Profit Factor</div><div className={`jsc-val ${pf>=1?'gp':'rp'}`}>{pf}</div><div className="jsc-sub">Gross profit ÷ loss</div></div>
                <div className="jn-stat-card"><div className="jsc-label">Largest Win</div><div className="jsc-val gp">{wins.length?fC(Math.max(...wins.map(t=>pnl(t)))):'--'}</div><div className="jsc-sub">{wins.length?wins.reduce((a,t)=>pnl(t)>pnl(a)?t:a,wins[0]).sym:''}</div></div>
                <div className="jn-stat-card"><div className="jsc-label">Largest Loss</div><div className="jsc-val rp">{losses.length?fC(Math.min(...losses.map(t=>pnl(t)))):'--'}</div><div className="jsc-sub">{losses.length?losses.reduce((a,t)=>pnl(t)<pnl(a)?t:a,losses[0]).sym:''}</div></div>
                <div className="jn-stat-card"><div className="jsc-label">Best Setup</div><div className="jsc-val" style={{fontSize:13,color:'#60a5fa'}}>{Object.entries(setupWR).sort((a,b)=>b[1].wr-a[1].wr)[0]?.[0]||'--'}</div><div className="jsc-sub">{Object.entries(setupWR).sort((a,b)=>b[1].wr-a[1].wr)[0]?Object.entries(setupWR).sort((a,b)=>b[1].wr-a[1].wr)[0][1].wr+'% win rate':'--'}</div></div>
                <div className="jn-stat-card"><div className="jsc-label">Total Trades</div><div className="jsc-val">{trades.length}</div><div className="jsc-sub">Avg {fC(trades.reduce((s,t)=>s+Math.abs(pnl(t)),0)/Math.max(trades.length,1))} per trade</div></div>
              </div>
              <div className="jn-charts-row">
                <div className="jn-chart-card">
                  <div className="jcc-title">P&L by Day of Week</div>
                  <div className="jn-bar-chart">{days.map(d=><div key={d.d} className="jn-bar-wrap"><div className="jn-bar" style={{height:Math.max(4,Math.abs(d.p)/maxD*70)+'px',background:d.p>=0?'#22c55e':'#ef4444'}}></div><div className="jn-bar-label">{d.d}</div></div>)}</div>
                  <div className="jn-day-pnls">{days.map(d=><span key={d.d} style={{fontSize:9,color:d.p>=0?'#22c55e':'#ef4444'}}>{d.d}: {fC(d.p)}</span>)}</div>
                </div>
                <div className="jn-chart-card">
                  <div className="jcc-title">Setup Win Rates</div>
                  <div className="jn-setup-list">{Object.entries(setupWR).sort((a,b)=>b[1].wr-a[1].wr).map(([s,d])=>(
                    <div key={s} className="jn-setup-row">
                      <span className="jn-setup-name">{s}</span>
                      <div className="jn-setup-bar"><div className="jn-setup-fill" style={{width:d.wr+'%',background:d.wr>=50?'#22c55e':'#ef4444'}}></div></div>
                      <span className="jn-setup-wr" style={{color:d.wr>=50?'#22c55e':'#ef4444'}}>{d.wr}%</span>
                      <span className="jn-setup-n">{d.n}t</span>
                    </div>
                  ))}</div>
                </div>
              </div>
              {Object.keys(emotionCount).length>0&&(
                <div className="jn-chart-card" style={{margin:'0 14px 14px'}}>
                  <div className="jcc-title">Emotion Tracker</div>
                  <div className="jn-emotion-grid">{Object.entries(emotionCount).sort((a,b)=>b[1]-a[1]).map(([e,c])=>(
                    <div key={e} className="jn-emotion-item">
                      <span className="jn-emotion-name">{e}</span>
                      <div className="jn-emotion-bar"><div className="jn-emotion-fill" style={{width:(c/trades.length*100)+'%'}}></div></div>
                      <span className="jn-emotion-count">{c}×</span>
                    </div>
                  ))}</div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* CALENDAR VIEW */}
      {view==='calendar'&&(
        <div className="jn-content">
          <div className="jn-cal-header"><div className="jn-cal-title">{monthNames[month]} {year}</div></div>
          <div className="jn-cal-grid">
            {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d=><div key={d} className="jn-cal-dow">{d}</div>)}
            {Array.from({length:fd}).map((_,i)=><div key={'e'+i} className="jn-cal-day empty"></div>)}
            {Array.from({length:dim}).map((_,i)=>{
              const d=i+1,ds=`${year}-${String(month+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`,data=dd[ds];
              return(<div key={d} className={`jn-cal-day${data?(data.p>0?' prof':' lose'):''}`}>
                <div className="jn-cal-num">{d}</div>
                {data&&<><div className={`jn-cal-pnl ${data.p>=0?'p':'n'}`}>{data.p>=0?'+':''}{fC(data.p)}</div><div className="jn-cal-trades">{data.n} trade{data.n>1?'s':''}</div></>}
              </div>);
            })}
          </div>
          <div className="jn-cal-summary">
            <div className="jn-cal-sum-card"><div className="jcs-label">Month P&L</div><div className={`jcs-val ${monthPnl>=0?'gp':'rp'}`}>{fC(monthPnl)}</div></div>
            <div className="jn-cal-sum-card"><div className="jcs-label">Green Days</div><div className="jcs-val gp">{greenDays}</div></div>
            <div className="jn-cal-sum-card"><div className="jcs-label">Red Days</div><div className="jcs-val rp">{redDays}</div></div>
            <div className="jn-cal-sum-card"><div className="jcs-label">Total Trades</div><div className="jcs-val">{trades.length}</div></div>
            <div className="jn-cal-sum-card"><div className="jcs-label">Win Rate</div><div className="jcs-val gp">{wr}%</div></div>
          </div>
        </div>
      )}

      {/* TRADE LOG */}
      {view==='trades'&&(
        <div className="jn-content" style={{padding:0,display:'flex',flexDirection:'column'}}>
          <div className="jn-trades-toolbar">
            <input className="jn-filter" placeholder="Symbol..." value={filterSym} onChange={e=>setFilterSym(e.target.value)}/>
            <select className="jn-filter-sel"><option>All Setups</option>{SETUPS.map(s=><option key={s}>{s}</option>)}</select>
            <select className="jn-filter-sel"><option>All Results</option><option>Wins</option><option>Losses</option></select>
            <span className="jn-trade-count">{filtered.length} trades · {fC(filtered.reduce((s,t)=>s+pnl(t),0))} P&L</span>
          </div>
          <div className="jn-trades-list">
            {filtered.length===0&&<div style={{textAlign:'center',padding:40,color:'#484f58',fontSize:12}}>No trades yet. Click "+ Log Trade" to add your first trade.</div>}
            {filtered.map(t=>{
              const p=pnl(t),rrT=t.sl?Math.abs((t.exit-t.entry)/(t.entry-t.sl)).toFixed(2):'--';
              return(
                <div key={t.id} className={`jn-trade-card ${p>0?'win':p<0?'loss':'be'}`}>
                  <div className="jtc-top">
                    <span className="jtc-sym">{t.sym}</span>
                    <span className={`jtc-side ${t.side==='long'?'long':'short'}`}>{t.side.toUpperCase()}</span>
                    <span className="jtc-setup">{t.setup}</span>
                    <span className="jtc-broker">{t.broker}</span>
                    <span className={`jtc-pnl ${p>=0?'p':'n'}`}>{p>=0?'+':''}{fC(p)}</span>
                    <button onClick={()=>deleteTrade(t.id)} style={{background:'none',border:'none',color:'#484f58',cursor:'pointer',fontSize:12,marginLeft:4}}>🗑</button>
                  </div>
                  <div className="jtc-stats">
                    <span>Date <b>{t.date}</b></span>
                    <span>Entry <b>${fN(t.entry)}</b></span>
                    <span>Exit <b>${fN(t.exit)}</b></span>
                    <span>Shares <b>{t.shares?.toLocaleString()}</b></span>
                    <span>R:R <b style={{color:rrT>=1?'#22c55e':'#ef4444'}}>{rrT}:1</b></span>
                    {t.sl&&<span>Stop <b>${fN(t.sl)}</b></span>}
                  </div>
                  {t.notes&&<div className="jtc-notes">{t.notes}</div>}
                  <div className="jtc-tags">
                    {(t.emotions||[]).map(e=><span key={e} className="jtc-tag emotion">{e}</span>)}
                    {(t.rules||[]).map(r=><span key={r} className="jtc-tag rule">{r}</span>)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* LOG TRADE MODAL */}
      {showModal&&(
        <div className="jn-overlay">
          <div className="jn-modal">
            <div className="jn-modal-header">
              <div className="jn-modal-title">Log a Trade</div>
              <button className="jn-modal-close" onClick={()=>setShowModal(false)}>✕</button>
            </div>
            <div className="jn-modal-body">
              <div className="jn-modal-grid">
                <div className="jmf"><label>Symbol</label><input value={mSym} onChange={e=>setMSym(e.target.value.toUpperCase())} placeholder="AAPL" maxLength={6}/></div>
                <div className="jmf"><label>Date</label><input type="date" value={mDate} onChange={e=>setMDate(e.target.value)}/></div>
                <div className="jmf"><label>Side</label><select value={mSide} onChange={e=>setMSide(e.target.value)}><option value="long">Long</option><option value="short">Short</option></select></div>
                <div className="jmf"><label>Setup</label><select value={mSetup} onChange={e=>setMSetup(e.target.value)}>{SETUPS.map(s=><option key={s}>{s}</option>)}</select></div>
                <div className="jmf"><label>Entry Price</label><input type="number" step="0.01" value={mEntry} onChange={e=>setMEntry(e.target.value)} placeholder="0.00"/></div>
                <div className="jmf"><label>Exit Price</label><input type="number" step="0.01" value={mExit} onChange={e=>setMExit(e.target.value)} placeholder="0.00"/></div>
                <div className="jmf"><label>Shares</label><input type="number" value={mShares} onChange={e=>setMShares(e.target.value)} placeholder="100"/></div>
                <div className="jmf"><label>Stop Loss</label><input type="number" step="0.01" value={mSl} onChange={e=>setMSl(e.target.value)} placeholder="0.00"/></div>
                <div className="jmf"><label>Broker</label><select value={mBroker} onChange={e=>setMBroker(e.target.value)}><option>thinkorswim</option><option>Webull</option><option>Interactive Brokers</option><option>Robinhood</option><option>Other</option></select></div>
                <div className="jmf"><label>Risk/Share</label><input readOnly value={mEntry&&mSl?'$'+Math.abs(parseFloat(mEntry)-parseFloat(mSl)).toFixed(2):'Auto-calc'} style={{color:'#484f58'}}/></div>
                <div className="jmf full"><label>Emotional State</label>
                  <div className="jmf-tags">{EMOTIONS.map(e=><button key={e} className={`jmf-tag emotion ${mEmotions.includes(e)?'sel':''}`} onClick={()=>toggleEmotion(e)}>{e}</button>)}</div>
                </div>
                <div className="jmf full"><label>Rule Checklist</label>
                  <div className="jmf-tags">{RULES.map(r=><button key={r} className={`jmf-tag rule ${mRules.includes(r)?'sel':''}`} onClick={()=>toggleRule(r)}>{r}</button>)}</div>
                </div>
                <div className="jmf full"><label>Notes</label>
                  <textarea value={mNotes} onChange={e=>setMNotes(e.target.value)} placeholder="Describe your setup, execution, and what you learned..."/>
                </div>
              </div>
            </div>
            <div className="jn-modal-footer">
              <button className="jn-btn" onClick={()=>setShowModal(false)}>Cancel</button>
              <button className="jn-btn prim" onClick={saveTrade} disabled={saving}>{saving?'Saving...':'Save Trade'}</button>
            </div>
          </div>
        </div>
      )}

      {/* CSV MODAL */}
      {showCSV&&(
        <div className="jn-overlay">
          <div className="jn-modal" style={{maxWidth:480}}>
            <div className="jn-modal-header"><div className="jn-modal-title">Import Trades from CSV</div><button className="jn-modal-close" onClick={()=>setShowCSV(false)}>✕</button></div>
            <div className="jn-modal-body" style={{padding:18}}>
              <div style={{fontSize:11,color:'#484f58',marginBottom:14,lineHeight:1.6}}>Select your broker format then upload your export file.</div>
              <div className="jn-csv-brokers">{['thinkorswim','Webull','Interactive Brokers','Robinhood','Generic CSV'].map(b=><button key={b} className="jn-csv-chip">{b}</button>)}</div>
              <div className="jn-csv-zone" onClick={fakeImport}>
                <div style={{fontSize:26,marginBottom:6}}>📂</div>
                <div style={{fontSize:11,color:'#8b949e'}}>Click to upload CSV or drag & drop</div>
              </div>
            </div>
            <div className="jn-modal-footer"><button className="jn-btn" onClick={()=>setShowCSV(false)}>Cancel</button><button className="jn-btn prim" onClick={fakeImport}>Import</button></div>
          </div>
        </div>
      )}

      {toast&&<div className="jn-toast" style={{background:toast.win?'#166534':'#7f1d1d',color:toast.win?'#86efac':'#fca5a5',borderColor:toast.win?'#22c55e44':'#ef444444'}}>{toast.msg}</div>}
    </div>
  );
}